import * as THREE from 'three';
import { CadObject } from '../../../models/cad-object.model';
import { BlockingUpBox, DFMCheck } from '../../../models/dfm-check.model';
import { DFM_CHECK_DEFINITIONS } from '../../../models/dfm-check-definitions';

const DEFAULT_COLOR: [number, number, number] = [0.827, 0.827, 0.827]; // LightGray
const WARNING_COLOR: [number, number, number] = [1.0, 0.0, 0.0]; // Red
const INFO_COLOR: [number, number, number] = [0.0, 0.0, 1.0]; // Blue
const BLOCKING_UP_COLOR = 0x00ff00; // Bright green

/**
 * Tracks Three.js objects added to a scene by the blocking-up visualisation so
 * they can be cleanly removed later without affecting other scene children.
 */
export interface DFMVisualizationState {
  blockingUpMeshes: THREE.LineSegments[];
}

export function createDFMVisualizationState(): DFMVisualizationState {
  return { blockingUpMeshes: [] };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Applies the visualisation for a single DFM check to the viewer.
 *
 * Resolves the correct rendering strategy (vertex colouring or blocking-up
 * wireframes) from DFM_CHECK_DEFINITIONS based on the check type, then
 * delegates to the appropriate internal renderer.
 *
 * @param dfmCheck     The DFM check to visualise.
 * @param cadObjects   All CadObjects currently in the viewer (used to find the
 *                     mesh that owns the check).
 * @param scene        The Three.js scene (needed only for blocking-up boxes).
 * @param state        Mutable state tracking blocking-up meshes for cleanup.
 */
export function displayDFMCheck(
  dfmCheck: DFMCheck,
  cadObjects: CadObject[],
  scene: THREE.Scene,
  state: DFMVisualizationState,
): void {
  const definition = DFM_CHECK_DEFINITIONS[dfmCheck.check_type];
  if (!definition.visualizationMode) return;

  const cadObject = cadObjects.find((obj) => obj.id === dfmCheck.cad_object_id);
  if (!cadObject) return;

  switch (definition.visualizationMode) {
    case 'blocking-up':
      if (dfmCheck.blocking_up) {
        addBlockingUpWireframes(dfmCheck.blocking_up, scene, state);
      }
      break;
    case 'holes-count':
      colorMeshByFaceProperty(
        cadObject,
        'holeDiameters',
        (v) => v > 0,
        INFO_COLOR,
      );
      break;
    case 'property': {
      const propertyName = definition.propertyMap(dfmCheck.material_id);
      if (propertyName) {
        colorMeshByFaceProperty(
          cadObject,
          propertyName,
          (v) => v === 1,
          WARNING_COLOR,
        );
      }
      break;
    }
  }
}

/**
 * Clears any active DFM visualisation and resets mesh colours to default gray.
 */
export function clearDFMVisualization(
  cadObjects: CadObject[],
  scene: THREE.Scene,
  state: DFMVisualizationState,
): void {
  removeBlockingUpWireframes(scene, state);

  for (const cadObject of cadObjects) {
    if (!cadObject.mesh) continue;

    const geometry = cadObject.mesh.geometry as THREE.BufferGeometry;
    const vertexCount = (
      geometry.getAttribute('position') as THREE.BufferAttribute
    ).count;
    const colors = new Float32Array(vertexCount * 3);
    fillColors(colors, DEFAULT_COLOR);
    applyVertexColors(
      geometry,
      cadObject.mesh.material as THREE.MeshStandardMaterial,
      colors,
    );
  }
}

// ---------------------------------------------------------------------------
// Vertex colouring
// ---------------------------------------------------------------------------

/**
 * Colours mesh vertices by reading a per-face property from the PLY-parsed
 * geometry userData, testing each value with `predicate`, and painting
 * matching triangles in `highlightColor` (the rest in default gray).
 *
 */
function colorMeshByFaceProperty(
  cadObject: CadObject,
  propertyName: string,
  predicate: (value: number) => boolean,
  highlightColor: [number, number, number],
): void {
  if (!cadObject.mesh) return;
  const geometry = cadObject.mesh.geometry as THREE.BufferGeometry;

  const faceProps = geometry.userData['faceProperties'] as
    Map<string, number[]> | undefined;
  const values =
    faceProps?.get(propertyName) ?? faceProps?.get(propertyName.toLowerCase());
  if (!values) return;

  const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute;
  const vertexCount = posAttr.count;
  const indexAttr = geometry.getIndex();
  const faceCount = indexAttr ? indexAttr.count / 3 : vertexCount / 3;

  const colors = new Float32Array(vertexCount * 3);
  fillColors(colors, DEFAULT_COLOR);

  const highlighted = new Uint8Array(vertexCount);

  if (indexAttr) {
    const indices = indexAttr.array;
    for (let f = 0; f < faceCount && f < values.length; f++) {
      if (!isNaN(values[f]) && predicate(values[f])) {
        highlighted[indices[f * 3]] = 1;
        highlighted[indices[f * 3 + 1]] = 1;
        highlighted[indices[f * 3 + 2]] = 1;
      }
    }
  } else {
    // Disconnected geometry: 3 vertices per face, in order.
    for (let f = 0; f < faceCount && f < values.length; f++) {
      if (!isNaN(values[f]) && predicate(values[f])) {
        highlighted[f * 3] = 1;
        highlighted[f * 3 + 1] = 1;
        highlighted[f * 3 + 2] = 1;
      }
    }
  }

  for (let i = 0; i < vertexCount; i++) {
    if (highlighted[i]) {
      colors[i * 3] = highlightColor[0];
      colors[i * 3 + 1] = highlightColor[1];
      colors[i * 3 + 2] = highlightColor[2];
    }
  }

  applyVertexColors(
    geometry,
    cadObject.mesh.material as THREE.MeshStandardMaterial,
    colors,
  );
}

function fillColors(colors: Float32Array, rgb: [number, number, number]): void {
  for (let i = 0; i < colors.length; i += 3) {
    colors[i] = rgb[0];
    colors[i + 1] = rgb[1];
    colors[i + 2] = rgb[2];
  }
}

function applyVertexColors(
  geometry: THREE.BufferGeometry,
  material: THREE.MeshStandardMaterial,
  colors: Float32Array,
): void {
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  material.vertexColors = true;
  material.needsUpdate = true;
}

// ---------------------------------------------------------------------------
// Blocking-up wireframes
// ---------------------------------------------------------------------------

function addBlockingUpWireframes(
  boxes: BlockingUpBox[],
  scene: THREE.Scene,
  state: DFMVisualizationState,
): void {
  removeBlockingUpWireframes(scene, state);

  for (const box of boxes) {
    const geo = new THREE.BoxGeometry(box.xSize, box.ySize, box.zSize);
    const edges = new THREE.EdgesGeometry(geo);
    const mat = new THREE.LineBasicMaterial({ color: BLOCKING_UP_COLOR });
    const wireframe = new THREE.LineSegments(edges, mat);
    wireframe.position.set(box.xCentre, box.yCentre, box.zCentre);
    scene.add(wireframe);
    state.blockingUpMeshes.push(wireframe);
  }
}

function removeBlockingUpWireframes(
  scene: THREE.Scene,
  state: DFMVisualizationState,
): void {
  for (const mesh of state.blockingUpMeshes) {
    scene.remove(mesh);
    mesh.geometry.dispose();
    if (mesh.material instanceof THREE.Material) {
      mesh.material.dispose();
    }
  }
  state.blockingUpMeshes = [];
}
